import { Box, type BoxProps, Flex, Text } from '@chakra-ui/react';
import { Notification } from 'ui-components';
import { SOCIAL_PLATFORMS } from '~/constants';
import { formatDate } from '~/utils';

import type {
  ContentDetailMetricsContentType,
  ContentDetailMetricsItemType,
} from './ContentDetailMetrics.types';
import { getValue } from './ContentDetailMetrics.utils';
import { PopoverGlossary } from './components/PopoverGlossary';

export type IContentDetailMetricsProps<
  C extends ContentDetailMetricsContentType,
> = BoxProps & {
  contentData: C;
  metrics: ContentDetailMetricsItemType<C>;
  isMetricsCollaborationFFEnbled?: boolean;
};

export function ContentDetailMetrics<
  C extends ContentDetailMetricsContentType,
>({
  contentData,
  metrics,
  isMetricsCollaborationFFEnbled,
  ...boxProps
}: IContentDetailMetricsProps<C>) {
  const isIGCollab =
    contentData?.platform === SOCIAL_PLATFORMS.instagram &&
    contentData?.isCollaboration;

  const igContentMapping = (channel: string) =>
    channel === 'post' ? 'still' : channel;

  const renderMetrics = (metrics: any) => (
    <Flex
      flex="0 0 100%"
      marginY={2}
      marginTop={'spacing-200'}
      direction={'column'}
      paddingY={'spacing-150'}
      paddingX={'spacing-125'}
      gap={'spacing-100'}
      borderRadius="lg"
      borderWidth={1}
      borderStyle="solid"
      borderColor="primary.100"
    >
      {Object.entries(metrics).map(([metricKey, metric]) => {
        const value = getValue(contentData, metric as any, metricKey);
        if (metric && value !== undefined) {
          const { icon: Icon, title } = metric as any;

          return (
            <Flex paddingX={'spacing-100'} alignItems="center" key={metricKey}>
              {Icon && <Icon color="primary.400" boxSize={4} marginRight={2} />}

              <Text color="primary.400" fontSize="lg" marginRight={2}>
                {title}
              </Text>

              <Text
                marginLeft="auto"
                color="primary.500"
                fontSize="lg"
                fontWeight={500}
              >
                {value}
              </Text>
            </Flex>
          );
        }

        return null;
      })}
    </Flex>
  );

  return (
    <>
      <Box {...boxProps}>
        {isMetricsCollaborationFFEnbled && isIGCollab && (
          <Notification
            title="Collaboration post"
            variant="warning"
            marginBottom="spacing-200"
            fontSize={'md'}
          >
            The metrics data displayed is pulled only from the Talent's IG
            account, and does not reflect any metrics from other collaborators.
          </Notification>
        )}
        <Text
          flex={1}
          color="primary.300"
          fontSize="sm"
          textTransform="uppercase"
          fontWeight="medium"
          lineHeight={1}
          letterSpacing={0.24}
        >
          {contentData.platform !== SOCIAL_PLATFORMS.instagram
            ? `${contentData.platform} metrics`
            : `${contentData.platform} ${igContentMapping(
                contentData.channel,
              )} metrics`}
        </Text>
        <Box marginTop={2}>
          {Object.entries(metrics).map(([metricKey, metric]) => {
            if (metric) {
              const { icon: Icon, metrics, title, isVisible } = metric;
              const value = getValue(contentData, metric, metricKey);

              // Hack to show likes and comment count for off-platform
              if (
                title === 'Engagements' &&
                (contentData.status === 'off-platform' ||
                  contentData.creator.status === 'off-platform') &&
                contentData.platform !== SOCIAL_PLATFORMS.tiktok
              ) {
                return renderMetrics(metrics);
              }

              if (value === undefined || value === null) {
                return null;
              }

              if (isVisible && !isVisible(contentData))
                return metrics ? renderMetrics(metrics) : null;

              return (
                <Flex
                  flexWrap="wrap"
                  alignItems="center"
                  key={metricKey}
                  paddingY={1}
                  paddingX={'spacing-100'}
                >
                  {Icon && (
                    <Icon color="primary.400" boxSize={4} marginRight={2} />
                  )}

                  <Text
                    flex={1}
                    color="primary.400"
                    fontSize="lg"
                    marginRight={2}
                    lineHeight={1.4}
                  >
                    {typeof title === 'function' ? title(contentData) : title}
                  </Text>

                  <Text
                    marginLeft="auto"
                    color="primary.500"
                    fontSize="lg"
                    fontWeight={500}
                    marginRight={'spacing-100'}
                  >
                    {value}
                  </Text>

                  {metrics && renderMetrics(metrics)}
                </Flex>
              );
            }
            return null;
          })}
        </Box>
      </Box>
      <Flex
        alignItems={'baseline'}
        justifyContent={'space-between'}
        marginRight={'spacing-100'}
        padding={'spacing-150'}
      >
        <PopoverGlossary metrics={metrics} contentData={contentData} />
        <Text fontSize="md" color="primary.300" textAlign="right">
          {contentData.insightLastUpdatedAt
            ? `Updated ${formatDate(contentData.insightLastUpdatedAt)}`
            : ''}
        </Text>
      </Flex>
    </>
  );
}
